import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { QRCodeCanvas } from "qrcode.react";
import { Receipt, Search } from "lucide-react";
import { api } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { PageHeader, EmptyState, StatusBadge, money, shortDate } from "../components/common";
import { fileUrl } from "../lib/api";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function IssuedReceipts() {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const { data: receipts = [], isLoading } = useQuery({
    queryKey: ["issued"],
    queryFn: async () => (await api.get("/receipts/issued")).data,
  });

  const filtered = receipts.filter((r) =>
    (r.items?.[0]?.name || "").toLowerCase().includes(q.toLowerCase()) ||
    r.claim_code.toLowerCase().includes(q.toLowerCase())
  );

  const claimUrl = selected ? `${window.location.origin}/claim/${selected.claim_code}` : "";

  return (
    <AppLayout>
      <PageHeader eyebrow="Retailer" title="Issued receipts" subtitle="Every digital receipt your store has created." />

      <div className="mb-5 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search by item or code…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="receipts-search" />
      </div>

      {isLoading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Receipt} title="No receipts found" subtitle="Try a different search, or issue a new receipt." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="divide-y divide-border">
            {filtered.map((r) => (
              <button key={r.id} onClick={() => setSelected(r)} data-testid={`issued-row-${r.id}`}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-accent">
                <div className="min-w-0">
                  <p className="truncate font-semibold">{r.items?.map((i) => i.name).join(", ") || "Purchase"}</p>
                  <p className="text-xs text-muted-foreground">Code {r.claim_code} · {shortDate(r.created_at)} · {r.customer_email || "unassigned"}</p>
                </div>
                <div className="flex shrink-0 items-center gap-4">
                  <StatusBadge status={r.status} />
                  <span className="font-mono text-sm font-bold tabular-nums">{money(r.total)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-md" data-testid="qr-dialog">
          <DialogHeader><DialogTitle className="font-heading">
            <span className="flex items-center gap-2.5">
              {selected?.store_logo_path && <img src={fileUrl(selected.store_logo_path)} alt="" className="h-7 w-7 rounded-lg object-cover" />}
              {selected?.store_name}
            </span>
          </DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <div className="rounded-2xl border border-border bg-white p-4">
              {selected && <QRCodeCanvas value={claimUrl} size={180} fgColor="#5C24FF" />}
            </div>
            <p className="font-mono text-lg font-bold tracking-widest">{selected?.claim_code}</p>
            <div className="w-full rounded-lg bg-secondary p-4 text-sm">
              {selected?.items?.map((i, idx) => (
                <div key={idx} className="flex justify-between"><span className="text-muted-foreground">{i.qty}× {i.name}</span><span className="font-mono">{money(i.price * i.qty)}</span></div>
              ))}
              <div className="mt-2 flex justify-between border-t border-border pt-2 font-bold"><span>Total</span><span className="font-mono">{money(selected?.total)}</span></div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
